import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { theme } from '../theme';
import { getLocalUser } from '../supabase';
import { getFreezes, getFreezePricePoints, cancelFreeze, verifyPrice, uploadFreezeEvidence, getFreezeEvidence } from '../api/atlast';
import PageBackground from './PageBackground';
import { ItineraryLeg } from '../ItineraryLeg';
import { useDestinationPhoto } from '../useDestinationPhoto';
import { formatMoney } from '../utils/dealExtras';
import { feeUsdNumber, feeLabel, feeParen } from '../utils/fee';
import ShareDeal from '../ShareDeal';
import TripExtras, { SectionLabel } from '../TripExtras';
import AppNav from '../AppNav';

// Same Skyscanner deep-link builder as FreezeTracker.js's buildVerifyUrl
// — kept in sync deliberately rather than imported, since FreezeTracker
// doesn't export it and duplicating one small pure function here is
// simpler than restructuring that file's exports. Date format is
// YYMMDD (2-digit year), matching DealScreen.js's verified
// buildSkyscannerUrl exactly.
function buildSkyscannerUrl(freeze) {
  if (!freeze.origin_code || !freeze.destination_code || !freeze.departure_date) return null;
  const toSkyscannerDate = (isoDate) => {
    const [y, m, d] = isoDate.split('-');
    return `${y.slice(2)}${m}${d}`;
  };
  let url = `https://www.skyscanner.com/transport/flights/${freeze.origin_code.toLowerCase()}/${freeze.destination_code.toLowerCase()}/`;
  url += `${toSkyscannerDate(freeze.departure_date)}/`;
  if (freeze.return_date) url += `${toSkyscannerDate(freeze.return_date)}/`;
  return url;
}

// Booking.com flights deep link — same builder as DealScreen.js's
// buildBookingComUrl (flights.booking.com's auto-search endpoint, not
// the generic pre-filled-but-not-run search form). This was missing
// from this screen entirely until now on an incorrect assumption that
// Booking.com's integration here was hotel-only — it isn't; the real
// flights deep link has existed on the Deal page all along. Adults/
// child-age params are omitted here (unlike DealScreen's version)
// since the enriched freeze object from getFreezes() doesn't carry
// passenger counts — defaults to Booking.com's own adult-only default,
// same limitation the Skyscanner link above already accepted.
function buildBookingComUrl(freeze) {
  if (!freeze.origin_code || !freeze.destination_code || !freeze.departure_date) return null;
  const originCode = freeze.origin_code.toUpperCase();
  const destCode = freeze.destination_code.toUpperCase();
  const path = `${originCode}.AIRPORT-${destCode}.CITY`;

  const params = new URLSearchParams();
  params.set('type', freeze.return_date ? 'ROUNDTRIP' : 'ONEWAY');
  params.set('adults', 1);
  params.set('cabinClass', 'ECONOMY');
  params.set('from', `${originCode}.AIRPORT`);
  params.set('to', `${destCode}.CITY`);
  params.set('depart', freeze.departure_date);
  if (freeze.return_date) params.set('return', freeze.return_date);
  params.set('currency_code', freeze.currency || 'CAD');

  return `https://flights.booking.com/flights/${path}/?${params.toString()}`;
}

// Per-tier cancel windows (Sep 9, per Ali's report) — mirrors
// FREEZE_CANCEL_WINDOW_HOURS_BY_DURATION in routes/freezes.py exactly.
// The backend remains the real enforcement; this is what decides
// whether the "Cancel freeze" button/countdown even shows here.
const FREEZE_CANCEL_WINDOW_HOURS_BY_DURATION = {
  48: 12,
  120: 24,
};
const DEFAULT_FREEZE_CANCEL_WINDOW_HOURS = 24;

// Direction 2 (order-tracking timeline) approved over the dashboard-grid
// alternative — reframes the whole screen around the 5-day journey as
// a vertical spine instead of a stack of similarly-styled boxes, closer
// to how a shipment/subscription tracker reads. Kept the pulse
// animation (chart's empty-state dot, the active timeline step's dot);
// dropped the shimmer sweep and the old countdown-bar fill animation,
// since neither has a home in this layout — the timeline's dot states
// (done/active/pending) now carry that "something is moving" job.
function FreezeDetailAnimations() {
  return (
    <style>{`
      @keyframes fd-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
      }
      .fd-pulse-dot { animation: fd-pulse 1.6s ease-in-out infinite; }
      /* fd-grid removed (Aug 17, per Ali's report) — the 2-column
         price-card/timeline layout it powered was replaced by the
         single-column section flow (status banner → itinerary →
         timeline → chart → intelligence → what's next). */
    `}</style>
  );
}

// Line chart version of FreezeTracker's FreezeBarChart — same data
// source (price_check_log points since the freeze started) and same
// "no fabricated trend line between raw checks would be honest" logic
// applies less here since this screen's job is specifically to show
// movement toward/away from the frozen price over the 5-day window, so
// a connecting line is the right call for this view (unlike the compact
// Dashboard card, where each check is shown as a standalone bar).
// Full chart, moved out of the cramped stat card into its own
// full-width section below the grid (per the approved mockup). Shows
// three honest things and nothing fabricated: (1) real pre-freeze
// history from price_check_log — this pin's own logged checks before
// the freeze existed, since a pin needs 10+ of those just to become
// freeze-eligible; (2) the real freeze-window line, same data as
// before; (3) a shaded "remaining days" zone with a real countdown —
// deliberately NOT an extrapolated price line, since no real
// prediction model exists (see the earlier decision to reject charging
// for "price prediction" for the same reason: faking confidence we
// don't have would be dishonest, not useful).
// resolveCurrencyPoints — shared between FreezeFullChart and
// FreezeInsights (Aug 14, per Ali's report) so both read the exact same
// currency-safe history instead of each re-implementing the same
// resolution logic and risking the two copies drifting apart — the
// same lesson this codebase already learned once from the formatTime
// production incident. Prefers price_converted (see
// migrations/add_price_converted_to_price_check_log.sql), falls back
// to the raw price only when its currency already matches the freeze's
// own currency, and drops the point entirely otherwise rather than
// plotting or reasoning over an unconverted number as if it belonged
// on the same axis as everything else.
function resolveCurrencyPoints(prePoints, points, targetCurrency) {
  const resolveValue = (p) => {
    if (p.price_converted != null) return p.price_converted;
    if (p.currency && p.currency === targetCurrency) return p.price;
    return null;
  };
  const pre = (prePoints || []).map(p => ({ t: new Date(p.checked_at).getTime(), price: resolveValue(p) })).filter(p => p.price != null);
  const win = (points || []).map(p => ({ t: new Date(p.checked_at).getTime(), price: resolveValue(p) })).filter(p => p.price != null);
  return { pre, win };
}

// ---------------------------------------------------------------------
// Price Watch chart + stats (Phase 2, Sep 22 — rebuilt from the
// approved interactive mockup). What changed from the Aug versions:
//   - The two result thresholds are drawn as a band: green "you don't pay"
//     zones beyond the lines, an amber fee range between them. Values
//     come from the watch record (locked at start; estimated at today's
//     rate for watches created before Phase 2), never recomputed here.
//   - The solid "during your watch" line now starts exactly at the
//     watch-start marker. The old version prepended the last pre-watch
//     check to the solid line as a "bridge", so it visibly started
//     before "freeze started". The bridge is now part of the dashed
//     pre-watch line and runs into the Day 0 point instead.
//   - Narrow containers (phones) get their own drawing size, fonts and
//     a shorter history window. The old fixed 1000px viewBox scaled its
//     12px labels down to ~4px on a phone.
//   - Every stat says its scope ("since you pinned", "during your
//     watch", "before your watch") and carries the currency.
//   - "N of M checks at or below your price" is replaced by where the
//     price sits against the $0 lines, which is what decides the result.
// Still true from before: no prediction line, ever. The remaining time
// is shaded and marked "not checked yet", nothing is drawn into it.
// ---------------------------------------------------------------------

const NARROW_BREAKPOINT = 560;

function useContainerWidth(ref) {
  const [width, setWidth] = useState(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const el = ref.current;
    const update = () => setWidth(el.getBoundingClientRect().width);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

function fmtSigned(value) {
  if (Math.abs(value) < 0.005) return '±0.00';
  return `${value > 0 ? '+' : '−'}${formatMoney(Math.abs(value))}`;
}

function fmtShortDateTime(t) {
  return new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' });
}

// Where the price sits against the result lines. Shared by the chart's
// status pill and anything else that needs the same verdict.
function watchPosition(price, frozenPrice, upAmount, downAmount) {
  if (price == null || frozenPrice == null || upAmount == null || downAmount == null) return null;
  const upper = Number(frozenPrice) + Number(upAmount);
  const lower = Number(frozenPrice) - Number(downAmount);
  if (price >= upper) return { zone: 'above', upper, lower, distance: price - upper };
  if (price <= lower) return { zone: 'below', upper, lower, distance: lower - price };
  if (price >= Number(frozenPrice)) return { zone: 'inside', upper, lower, nearest: 'upper', distance: upper - price };
  return { zone: 'inside', upper, lower, nearest: 'lower', distance: price - lower };
}

function computeKPIs(pre, win, frozenPrice, comparisonPrice, currency, isActive) {
  const all = [...pre, ...win];
  const kpis = [];
  if (all.length === 0) return kpis;
  const fmtDate = (t) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const lowest = all.reduce((min, p) => (p.price < min.price ? p : min), all[0]);
  kpis.push({ tone: 'down', value: formatMoney(lowest.price), label: `Lowest since you pinned · ${currency} · ${fmtDate(lowest.t)}` });

  const highest = all.reduce((max, p) => (p.price > max.price ? p : max), all[0]);
  if (highest.price > lowest.price) {
    kpis.push({ tone: 'up', value: formatMoney(highest.price), label: `Highest since you pinned · ${currency} · ${fmtDate(highest.t)}` });
  }

  if (comparisonPrice != null && frozenPrice != null) {
    kpis.push({
      tone: 'neutral',
      value: fmtSigned(comparisonPrice - frozenPrice),
      label: `${isActive ? 'Live' : 'Result'} vs. starting price · ${currency}`
    });
  }

  if (win.length > 0) {
    kpis.push({ tone: 'neutral', value: `${win.length} check${win.length === 1 ? '' : 's'}`, label: 'During your watch · every 2 hours' });
  }
  return kpis.slice(0, 4);
}

// Plain facts only, each labelled with the period it covers. No
// "moved a fair amount", no "good sign": the reader decides.
function computeNarratives(pre, win, frozenPrice, currency) {
  const narratives = [];
  const fmtDate = (t) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  if (win.length >= 2) {
    let changes = 0;
    let biggest = 0;
    for (let i = 1; i < win.length; i++) {
      const d = Math.abs(win[i].price - win[i - 1].price);
      if (d >= 0.005) { changes++; biggest = Math.max(biggest, d); }
    }
    narratives.push({
      scope: 'During your watch',
      text: changes === 0
        ? `The price hasn't changed in ${win.length} checks.`
        : `The price changed ${changes} time${changes === 1 ? '' : 's'} in ${win.length} checks, never by more than ${formatMoney(biggest)} ${currency}.`
    });
  }

  if (pre.length >= 1) {
    const preMin = Math.min(...pre.map(p => p.price));
    const preMax = Math.max(...pre.map(p => p.price));
    let text = pre.length === 1
      ? `1 check on ${fmtDate(pre[0].t)} found ${formatMoney(pre[0].price)} ${currency}.`
      : `${pre.length} checks from ${fmtDate(pre[0].t)} ranged from ${formatMoney(preMin)} to ${formatMoney(preMax)} ${currency}.`;
    if (pre.length > 1 && frozenPrice != null) {
      const tol = Math.max(0.005, Number(frozenPrice) * 0.0005);
      if (Number(frozenPrice) >= preMax - tol) text += ' Your starting price was the highest of them.';
      else if (Number(frozenPrice) <= preMin + tol) text += ' Your starting price was the lowest of them.';
    }
    narratives.push({ scope: 'Before your watch', text });
  }
  return narratives;
}

function FreezeInsights({ prePoints, points, frozenPrice, frozenCurrency, comparisonPrice, isActive }) {
  const { pre, win } = resolveCurrencyPoints(prePoints, points, frozenCurrency);
  const kpis = computeKPIs(pre, win, frozenPrice, comparisonPrice, frozenCurrency, isActive);
  const narratives = computeNarratives(pre, win, frozenPrice, frozenCurrency);
  if (kpis.length === 0 && narratives.length === 0) return null;

  const toneColor = { down: theme.success, up: '#B4452A', neutral: theme.structural };

  return (
    <>
    <SectionLabel style={{ margin: '28px 0 14px' }}>What our checks show</SectionLabel>
    <div style={{ background: '#fff', border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '14px', padding: '22px' }}>
      <p style={{ fontSize: '12.5px', color: theme.textMuted, margin: '0 0 16px' }}>
        Facts from this trip's own checks. Not a prediction.
      </p>

      {kpis.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: narratives.length > 0 ? '16px' : 0 }}>
          {kpis.map((k, i) => (
            <div key={i} style={{ border: `2px solid ${theme.divider}`, borderTop: `4px solid ${toneColor[k.tone]}`, padding: '12px' }}>
              <div style={{ fontFamily: theme.fontMono, fontSize: '17px', fontWeight: 800, color: toneColor[k.tone] }}>{k.value}</div>
              <div style={{ fontSize: '12px', color: '#3D4A56', marginTop: '4px', lineHeight: 1.4 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      {narratives.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {narratives.map((n, i) => (
            <div key={i} style={{ background: theme.bg, padding: '11px 13px' }}>
              <div style={{ fontFamily: theme.fontMono, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.textMuted, marginBottom: '3px' }}>{n.scope}</div>
              <p style={{ fontSize: '13px', color: theme.textPrimary, lineHeight: 1.5, margin: 0 }}>{n.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
}

// Where-you-stand card (Oct 10, per Ali's approved mockup). Lives at the top
// of the page now, right under the header, so the one sentence a quick glance
// needs is the first thing read.
function WatchVerdict({ comparisonPrice, frozenPrice, thresholdUp, thresholdDown, feeText, currency }) {
  const position = watchPosition(comparisonPrice, frozenPrice, thresholdUp, thresholdDown);
  if (!position) return null;
  const inside = position.zone === 'inside';
  return (
    <div style={{ background: '#fff', border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', borderRadius: '14px', padding: '16px 20px', marginTop: '16px', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontFamily: theme.fontMono, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#4A5F78' }}>Where you stand right now</div>
        <div style={{ fontSize: '19px', fontWeight: 800, color: theme.structural, marginTop: '3px', lineHeight: 1.3 }}>
          {inside
            ? `Inside the fee range. If it ended now, you'd pay ${feeText || 'the Price Watch fee'}.`
            : "Outside the fee range. If it ended now, you'd pay nothing."}
        </div>
        <div style={{ fontSize: '12.5px', color: theme.textMuted, marginTop: '4px' }}>
          You pay nothing if it ends above {formatMoney(position.upper)} or below {formatMoney(position.lower)} {currency}. Only the price at the end counts.
        </div>
      </div>
      <span style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', background: inside ? '#F4C11C' : '#E3F0E8', color: inside ? '#1E3A5F' : '#17663A', padding: '7px 12px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
        {inside ? 'Inside fee range' : 'Outside fee range'}
      </span>
    </div>
  );
}

function FreezeFullChart({ prePoints, points, frozenPrice, frozenCurrency, startedAt, expiresAt, now, fmtDate,
  thresholdUp, thresholdDown, thresholdsEstimated, comparisonPrice, status, feeText }) {
  const { pre: preAll, win } = resolveCurrencyPoints(prePoints, points, frozenCurrency);
  const isActive = now.getTime() < expiresAt.getTime() && status === 'active';
  const wrapRef = useRef(null);
  const containerWidth = useContainerWidth(wrapRef);
  const narrow = containerWidth != null && containerWidth < NARROW_BREAKPOINT;
  const [hover, setHover] = useState(null);

  const cur = frozenCurrency || '';
  const day0 = Number(frozenPrice);
  const hasThresholds = thresholdUp != null && thresholdDown != null;
  const upper = hasThresholds ? day0 + Number(thresholdUp) : null;
  const lower = hasThresholds ? day0 - Number(thresholdDown) : null;

  if (preAll.length === 0 && win.length === 0) {
    return (
      <div ref={wrapRef} style={{ textAlign: 'center', padding: '40px 12px', background: 'rgba(30,58,95,0.05)' }}>
        <div className="fd-pulse-dot" style={{ width: '12px', height: '12px', borderRadius: '50%', background: theme.success, margin: '0 auto 10px' }} />
        <div style={{ fontSize: '13px', color: theme.textPrimary, fontWeight: 600 }}>Waiting for the first check...</div>
        <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '4px' }}>We check every 2 hours during your watch. The first one will appear here.</div>
      </div>
    );
  }

  // Drawing size per layout. The SVG is drawn at roughly the size it's
  // shown, so font sizes below are real on-screen sizes.
  const L = narrow
    ? { w: Math.max(300, Math.round(containerWidth)), h: 320, padL: 46, padR: 8, padTop: 54, padBottom: 34, fs: 11, fsS: 10.5, historyMs: 48 * 3600 * 1000 }
    : { w: 1000, h: 380, padL: 70, padR: 20, padTop: 58, padBottom: 40, fs: 13, fsS: 12, historyMs: Math.max(24 * 3600 * 1000, expiresAt.getTime() - startedAt.getTime()) };

  const startT = startedAt.getTime();
  const endT = expiresAt.getTime();
  const nowT = Math.min(Math.max(now.getTime(), startT), endT);

  // Phones show the last 2 days before the watch so the watch window
  // has room for its labels; desktop shows everything since pinning.
  const earliest = preAll.length > 0 ? preAll[0].t : (win.length > 0 ? win[0].t : startT);
  const domainStart = L.historyMs ? Math.max(earliest, startT - L.historyMs) : earliest;
  const domainEnd = endT + (endT - Math.min(domainStart, startT)) * 0.02;
  const pre = preAll.filter(p => p.t >= domainStart);
  const preBeforeDomain = preAll.filter(p => p.t < domainStart);
  const lead = preBeforeDomain.length > 0 ? [preBeforeDomain[preBeforeDomain.length - 1]] : [];

  // Line labels (Sep 22, per Ali): plain "You don't pay…" wording, left-
  // aligned at the watch start like every other in-window label, and
  // wrapped to 2 lines (or shortened) so they never spill outside the
  // watch window. "ends", not "goes": only the price at the end counts.
  const winPx = ((endT - startT) / (domainEnd - domainStart)) * ((L.w - L.padR) - L.padL);
  const charW = L.fsS * 0.57;
  const fitLines = (options) => options.find(o => Math.max(...o.map(l => l.length)) * charW <= winPx - 16) || options[options.length - 1];
  const upLines = hasThresholds ? fitLines([
    [`You don't pay if the price ends above ${formatMoney(upper)} ${cur}`],
    ['You don\'t pay if the price', `ends above ${formatMoney(upper)} ${cur}`],
    ['You don\'t pay above', `${formatMoney(upper)} ${cur}`],
  ]) : [];
  const downLines = hasThresholds ? fitLines([
    [`You don't pay if the price ends below ${formatMoney(lower)} ${cur}`],
    ['You don\'t pay if the price', `ends below ${formatMoney(lower)} ${cur}`],
    ['You don\'t pay below', `${formatMoney(lower)} ${cur}`],
  ]) : [];
  const lineH = L.fsS + 4;
  const labelNeedPx = Math.max(upLines.length, downLines.length, 1) * lineH + 10;

  const values = [...pre.map(p => p.price), ...win.map(p => p.price), day0];
  if (hasThresholds) values.push(upper, lower);
  let yMin = Math.min(...values);
  let yMax = Math.max(...values);
  const span = (yMax - yMin) || Math.max(1, Math.abs(day0) * 0.01);
  yMin -= span * 0.06;
  yMax += span * 0.06;
  if (hasThresholds) {
    // Make the strips above the upper line and below the lower line tall
    // enough (in pixels) for their labels: solve for the y-range where
    // each strip is at least labelNeedPx of the plot height. 8 passes
    // (was 3, Sep 23) — 3 left real watches with tight fee bands a few
    // percent short of the reserved room; 8 converges to within
    // <0.001% in every case tested, at negligible extra cost (this
    // runs once per render, not per frame).
    const plotH = (L.h - L.padBottom) - L.padTop;
    const k = Math.min(0.35, labelNeedPx / plotH);
    for (let i = 0; i < 8; i++) {
      yMax = Math.max(yMax, (upper - k * yMin) / (1 - k));
      yMin = Math.min(yMin, (lower - k * yMax) / (1 - k));
    }
  }

  const plotL = L.padL, plotR = L.w - L.padR, plotT = L.padTop, plotB = L.h - L.padBottom;
  const xOf = (t) => plotL + ((t - domainStart) / (domainEnd - domainStart)) * (plotR - plotL);
  const yOf = (v) => plotT + ((yMax - v) / (yMax - yMin)) * (plotB - plotT);

  const sx = xOf(startT), ex = xOf(endT), nx = xOf(nowT);
  const dy = yOf(day0);
  const uy = hasThresholds ? yOf(upper) : null;
  const ly = hasThresholds ? yOf(lower) : null;

  // Dashed history runs into the Day 0 point at the watch start; the
  // solid line starts there too, so nothing solid appears before it.
  const preLine = [...lead, ...pre, { t: startT, price: day0 }];
  const winLine = [{ t: startT, price: day0 }, ...win.filter(p => p.t >= startT)];
  const toPts = (arr) => arr.map(p => `${xOf(p.t).toFixed(1)},${yOf(p.price).toFixed(1)}`).join(' ');

  const lastPoint = win.length > 0 ? win[win.length - 1] : null;

  // Gridlines: 4 round-number ticks.
  const rawStep = (yMax - yMin) / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const step = [1, 2, 2.5, 5, 10].map(m => m * mag).find(s => s >= rawStep) || rawStep;
  const yTicks = [];
  for (let v = Math.ceil(yMin / step) * step; v <= yMax; v += step) yTicks.push(v);
  const fmtTick = (v) => (step >= 1 ? Math.round(v).toLocaleString('en-US') : v.toFixed(2));

  // Day ticks (midnight), thinned to fit.
  const dayTicks = [];
  const firstMidnight = new Date(domainStart);
  firstMidnight.setHours(24, 0, 0, 0);
  for (let t = firstMidnight.getTime(); t <= domainEnd; t += 24 * 3600 * 1000) dayTicks.push(t);
  const maxTicks = narrow ? 5 : 10;
  const tickEvery = Math.max(1, Math.ceil(dayTicks.length / maxTicks));
  const shownTicks = dayTicks.filter((_, i) => i % tickEvery === 0);

  // Countdown lane.
  const msLeft = endT - now.getTime();
  const hoursLeft = Math.max(0, Math.ceil(msLeft / 3600000));
  const resultWhen = new Date(endT).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: new Date(endT).getMinutes() ? '2-digit' : undefined });
  const countdown = isActive
    ? (hoursLeft < 24 ? `${hoursLeft} hour${hoursLeft === 1 ? '' : 's'} left` : `${Math.ceil(hoursLeft / 24)} days left`) + ` · result ${resultWhen}`
    : `Result ${resultWhen}`;

  // Day 0 label goes above its line when there's room below the upper
  // line, otherwise just under it, so it never sits on the upper label.
  const day0LabelAbove = !hasThresholds || (dy - uy) >= lineH + 10;

  // Hover / tap — snaps to the nearest real check.
  const hoverPoints = [
    ...pre.map(p => ({ ...p, kind: 'before' })),
    ...win.map(p => ({ ...p, kind: 'watch' }))
  ].map(p => ({ ...p, x: xOf(p.t), y: yOf(p.price) }));

  function handlePointer(clientX, target) {
    const rect = target.getBoundingClientRect();
    const svgX = ((clientX - rect.left) / rect.width) * L.w;
    let nearest = null;
    let best = Infinity;
    for (const p of hoverPoints) {
      const d = Math.abs(p.x - svgX);
      if (d < best) { best = d; nearest = p; }
    }
    if (nearest) setHover(nearest);
  }

  const position = watchPosition(comparisonPrice, frozenPrice, thresholdUp, thresholdDown);

  return (
    <div ref={wrapRef}>
      {/* Status pill: where the price sits against the no-fee lines. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
        {comparisonPrice != null && (
          <span style={{ fontFamily: theme.fontMono, fontSize: '13px', fontWeight: 800, background: theme.structural, color: '#fff', padding: '6px 10px' }}>
            {isActive ? 'Now' : 'At result'} {formatMoney(comparisonPrice)} {cur}
          </span>
        )}
        {!isActive && status === 'refunded' && (
          <span style={{ fontSize: '12.5px', fontWeight: 800, background: '#1E3A5F', color: '#F4C11C', padding: '6px 10px' }}>You paid nothing</span>
        )}
        {!isActive && status === 'kept' && (
          <span style={{ fontSize: '12.5px', fontWeight: 800, background: '#F4C11C', color: '#1E3A5F', padding: '6px 10px' }}>Fee charged</span>
        )}
        {isActive && position && position.zone === 'inside' && (
          <>
            <span style={{ fontSize: '12.5px', fontWeight: 800, background: '#F4C11C', color: '#1E3A5F', padding: '6px 10px' }}>Inside the fee range</span>
            <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#3D4A56' }}>
              You don't pay if it ends above {formatMoney(position.upper)} or below {formatMoney(position.lower)} {cur}
            </span>
          </>
        )}
        {isActive && position && position.zone !== 'inside' && (
          <>
            <span style={{ fontSize: '12.5px', fontWeight: 800, background: '#1E3A5F', color: '#F4C11C', padding: '6px 10px' }}>
              If it ends here, you don't pay
            </span>
            <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#3D4A56' }}>{position.zone === 'above' ? `At or above ${formatMoney(position.upper)} ${cur}` : `At or below ${formatMoney(position.lower)} ${cur}`}</span>
          </>
        )}
      </div>

      <div style={{ position: 'relative' }}>
        <svg width="100%" viewBox={`0 0 ${L.w} ${L.h}`} role="img" style={{ display: 'block', cursor: 'crosshair', touchAction: 'pan-y' }}
          aria-label={`Price history in ${cur}: ${pre.length} checks before your watch and ${win.length} during it.${hasThresholds ? ` You pay nothing if the price ends above ${formatMoney(upper)} or below ${formatMoney(lower)}.` : ''}`}
          onMouseMove={(e) => handlePointer(e.clientX, e.currentTarget)}
          onMouseLeave={() => setHover(null)}
          onTouchStart={(e) => handlePointer(e.touches[0].clientX, e.currentTarget)}
          onTouchMove={(e) => handlePointer(e.touches[0].clientX, e.currentTarget)}>
          <defs>
            <pattern id="watch-hatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="7" stroke="#D9AC1F" strokeWidth="2.5" />
            </pattern>
            <clipPath id="watch-plot-clip">
              <rect x={plotL} y={plotT} width={plotR - plotL} height={plotB - plotT} />
            </clipPath>
          </defs>

          {yTicks.map(v => (
            <g key={v}>
              <line x1={plotL} y1={yOf(v)} x2={plotR} y2={yOf(v)} stroke="#E4E9ED" strokeWidth="1" />
              <text x={plotL - 8} y={yOf(v) + 4} textAnchor="end" fontSize={L.fsS} fill={theme.textMuted}>{fmtTick(v)}</text>
            </g>
          ))}

          {hasThresholds && (
            <g clipPath="url(#watch-plot-clip)">
              <rect x={sx} y={plotT} width={ex - sx} height={Math.max(0, uy - plotT)} fill="#EAF1F8" />
              <rect x={sx} y={ly} width={ex - sx} height={Math.max(0, plotB - ly)} fill="#EAF1F8" />
              <rect x={sx} y={uy} width={ex - sx} height={Math.max(0, ly - uy)} fill="#F4C11C" />
              {isActive && <rect x={nx} y={uy} width={Math.max(0, ex - nx)} height={Math.max(0, ly - uy)} fill="url(#watch-hatch)" />}
            </g>
          )}
          {!hasThresholds && isActive && (
            <rect x={nx} y={plotT} width={Math.max(0, ex - nx)} height={plotB - plotT} fill="url(#watch-hatch)" opacity="0.6" />
          )}

          {hasThresholds && (
            <>
              <line x1={sx} y1={uy} x2={ex} y2={uy} stroke={theme.structural} strokeWidth="2.2" />
              <line x1={sx} y1={ly} x2={ex} y2={ly} stroke={theme.structural} strokeWidth="2.2" />
              {(ly - dy) > L.fsS * (day0LabelAbove ? 5 : 8) && (
                <text x={sx + 8} y={Math.max((dy + ly) / 2, dy + L.fsS * 3)} fontSize={L.fsS} fontWeight="600" fill="#1E3A5F">
                  <tspan x={sx + 8} dy="0" fontWeight="800">FEE ZONE</tspan>
                  <tspan x={sx + 8} dy={L.fsS + 3}>you pay if it ends in here</tspan>
                </text>
              )}
            </>
          )}

          <line x1={sx} y1={dy} x2={ex} y2={dy} stroke={theme.structural} strokeWidth="1" strokeDasharray="2 3" opacity="0.7" />
          <line x1={sx} y1={plotT - 6} x2={sx} y2={plotB} stroke={theme.structural} strokeWidth="1.4" strokeDasharray="3 3" opacity="0.6" />
          <text x={sx + 8} y={L.padTop - 18} fontSize={L.fs} fontWeight="700" fill={theme.structural}>watch started</text>
          <text x={Math.min(ex, L.w - 2)} y={16} textAnchor="end" fontSize={L.fs} fontWeight="700" fill={theme.structural}>{countdown}</text>

          {preLine.length > 1 && (
            <polyline points={toPts(preLine)} fill="none" stroke="#9FB3C8" strokeWidth="2.4" strokeDasharray="6 4"
              strokeLinecap="round" strokeLinejoin="round" clipPath="url(#watch-plot-clip)" />
          )}
          {winLine.length > 1 && (
            <polyline points={toPts(winLine)} fill="none" stroke={theme.structural} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          )}

          {hasThresholds && (() => {
            // The no-fee labels sit at the outer edge of their zones (top of
            // the upper zone, bottom of the lower one), away from the price
            // line, which starts at the Day 0 level in the middle. Only if
            // a zone is too thin for that do they fall back to hugging
            // their dashed line. Drawn after the lines, with a halo.
            const upTopY = plotT + L.fsS + 4;
            const upFits = upTopY + (upLines.length - 1) * lineH <= uy - 6;
            const upBase = upFits ? upTopY : uy - 7 - (upLines.length - 1) * lineH;
            const dnBottomY = plotB - 6;
            const dnFits = dnBottomY - (downLines.length - 1) * lineH >= ly + L.fsS + 4;
            const dnBase = dnFits ? dnBottomY - (downLines.length - 1) * lineH : ly + L.fsS + 5;
            return (
              <>
                {upLines.map((line, i) => (
                  <text key={`up${i}`} x={sx + 8} y={upBase + i * lineH} fontSize={L.fsS} fontWeight="700" fill={theme.structural}
                    stroke="#EAF1F8" strokeWidth="4" strokeLinejoin="round" paintOrder="stroke">{line}</text>
                ))}
                {downLines.map((line, i) => (
                  <text key={`dn${i}`} x={sx + 8} y={dnBase + i * lineH} fontSize={L.fsS} fontWeight="700" fill={theme.structural}
                    stroke="#EAF1F8" strokeWidth="4" strokeLinejoin="round" paintOrder="stroke">{line}</text>
                ))}
              </>
            );
          })()}
          <text x={sx + 8} y={day0LabelAbove ? dy - 8 : dy + L.fsS + 6} fontSize={L.fsS} fontWeight="700" fill={theme.structural}
            stroke="#F4C11C" strokeWidth="4" strokeLinejoin="round" paintOrder="stroke">Start {formatMoney(day0)}</text>
          {/* Latest check: dot only. Its price is in the "Now" pill above
              the chart, so a text label here only added clutter. */}
          {lastPoint && (
            <circle cx={xOf(lastPoint.t)} cy={yOf(lastPoint.price)} r="7" fill="#1E3A5F" stroke="#fff" strokeWidth="3" />
          )}

          {shownTicks.map(t => (
            <text key={t} x={xOf(t)} y={L.h - 12} textAnchor="middle" fontSize={L.fsS}
              fontWeight={Math.abs(t - new Date(endT).setHours(0, 0, 0, 0)) < 3600000 ? 700 : 500}
              fill={Math.abs(t - new Date(endT).setHours(0, 0, 0, 0)) < 3600000 ? theme.structural : theme.textMuted}>
              {fmtDate(new Date(t))}
            </text>
          ))}
          <line x1={plotL} y1={plotB} x2={plotR} y2={plotB} stroke="#C9D3DB" strokeWidth="1" />

          {hover && (
            <>
              <line x1={hover.x} y1={plotT} x2={hover.x} y2={plotB} stroke={theme.action} strokeWidth="2" />
              <circle cx={hover.x} cy={hover.y} r="6" fill={theme.action} stroke="#fff" strokeWidth="2" />
            </>
          )}
        </svg>

        {hover && (
          <div style={{
            position: 'absolute', pointerEvents: 'none',
            left: `${Math.min(Math.max((hover.x / L.w) * 100, 14), 86)}%`, top: `${(hover.y / L.h) * 100}%`,
            transform: 'translate(-50%, calc(-100% - 12px))', background: theme.structural, color: '#fff',
            padding: '8px 10px', fontSize: '12px', lineHeight: 1.45, whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.22)'
          }}>
            <div style={{ color: '#C3CFDA' }}>{fmtShortDateTime(hover.t)}</div>
            <div style={{ fontFamily: theme.fontMono, fontWeight: 800, fontSize: '13px' }}>{formatMoney(hover.price)} {cur}</div>
            <div style={{ color: theme.action, fontWeight: 700 }}>
              {hover.kind === 'watch' ? `vs start: ${fmtSigned(hover.price - day0)}` : 'before your watch'}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', marginTop: '10px', fontSize: '12px', color: theme.textPrimary, fontWeight: 600 }}>
        {pre.length > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '18px', borderTop: '2px dashed #9FB3C8' }} />before your watch</span>}
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '18px', height: '3px', background: theme.structural }} />during your watch</span>
        {hasThresholds && <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '18px', borderTop: `2px solid ${theme.structural}` }} />no-fee lines</span>}
        {hasThresholds && <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '14px', height: '10px', background: '#F4C11C', border: '1px solid #1E3A5F' }} />fee zone</span>}
        {hasThresholds && <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '14px', height: '10px', background: '#EAF1F8' }} />you don't pay</span>}
        {isActive && <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '14px', height: '10px', background: 'repeating-linear-gradient(45deg, #D9AC1F 0 2px, #F4C11C 2px 5px)' }} />not checked yet</span>}
      </div>

      <p style={{ margin: '14px 0 0', padding: '10px 12px', background: theme.bg, fontSize: '12px', lineHeight: 1.5, color: theme.textMuted }}>
        Your result compares the price at the end of the watch with your starting price. Crossing a line earlier doesn't decide it; where it ends does.
        {thresholdsEstimated && hasThresholds ? ' The no-fee lines for this watch are shown at today\'s exchange rate, so they may differ slightly from the amounts used for your result.' : ''}
      </p>
    </div>
  );
}

// Narrative beats — turns "Day N of [total]" into a short story
// instead of just a number, without inventing anything not already
// true. totalDays parameterized (Sep 9, per Ali's report) rather than
// assuming 5 — for the 48-hour/2-day tier, day 2 IS the final/
// resolution day, not a mid-freeze checkpoint, but "still holding" and
// "no change yet" both stay accurate regardless of which tier this is,
// so the wording itself doesn't need a second version — only the math
// in the fallback line did.
function narrativeBeat(dayOfFive, heldSoFar, totalDays) {
  if (dayOfFive <= 1) return { title: 'Watching', body: "We're checking this price every 2 hours." };
  if (heldSoFar === false) return { title: 'Price moved', body: 'See the chart for where it stands now.' };
  const remaining = totalDays - dayOfFive;
  if (remaining <= 0) return { title: 'Last day', body: 'Your result lands within 24 hours.' };
  if (dayOfFive === 2) return { title: 'Still watching', body: 'No change from your starting price so far.' };
  if (remaining === 1) return { title: '1 day to go', body: "You'll get an email with your result." };
  if (dayOfFive / totalDays >= 0.4 && dayOfFive / totalDays <= 0.6) return { title: 'Halfway there', body: `${dayOfFive - 1} days down, ${remaining} to go.` };
  return { title: `${remaining} days to go`, body: 'Still checking every 2 hours.' };
}

function FreezeDetailScreen() {
  const { state } = useLocation();
  const navigate = useNavigate();
  // Real deep link support (Sep 23, per Ali's report — every Price
  // Watch email's "See your watch" link pointed at the dashboard
  // instead of this screen, because this screen only ever read
  // freezeId/userId from router state, which is only ever set by
  // in-app navigation — an email opened fresh (a new tab, a different
  // device) arrives with no state at all. The URL param is the new,
  // real entry point; state stays the primary one for in-app
  // navigation, unchanged, including the justCreated/fromDeal/pin
  // flags below, none of which apply to an email-opened session.
  const { freezeId: urlFreezeId } = useParams();
  const freezeId = urlFreezeId || state?.freezeId;

  // userId: taken from state when present (the normal in-app path —
  // zero extra cost, unchanged from before). Only resolved from the
  // live session when this screen was opened via the URL param with
  // no state at all (state?.userId is never set that way).
  const [sessionUserId, setSessionUserId] = useState(null);
  // Distinct from "no userId yet" — the redirect guard further down
  // (originally written to bounce a direct URL hit straight back to
  // the Dashboard) can't tell "genuinely missing" apart from "still
  // resolving the session" without this. Without it, that guard fires
  // on the very first render of every email-opened link, before
  // getUser() even has a chance to resolve, and sends the person to
  // the Dashboard instead of the watch screen the email promised —
  // silently undoing this whole fix.
  const [sessionChecked, setSessionChecked] = useState(!urlFreezeId || !!state?.userId);
  useEffect(() => {
    if (state?.userId || !urlFreezeId) return;
    let cancelled = false;
    getLocalUser().then(({ data }) => {
      if (cancelled) return;
      setSessionUserId(data?.user?.id || null);
      setSessionChecked(true);
    }).catch(() => { if (!cancelled) setSessionChecked(true); });
    return () => { cancelled = true; };
  }, [state?.userId, urlFreezeId]);
  const userId = state?.userId || sessionUserId;
  const resolvingSession = urlFreezeId && !state?.userId && !sessionChecked;
  // Gates the one-time hero celebration (shimmer header + "Price
  // frozen!" pill) — only the two entry points where a freeze was just
  // paid for (DealScreen's pitch, Dashboard's button) set this; a
  // revisit via FreezeTracker's card click never re-triggers it.
  const justCreated = !!state?.justCreated;
  // Round-trip navigation: when this screen is reached FROM a deal page
  // (the freeze-status banner or the pitch card, both pass fromDeal+pin
  // now), the back link should return there instead of only ever
  // dead-ending at the Dashboard — same principle as any e-commerce
  // "back to product" link on an order-status page.
  const fromDeal = !!state?.fromDeal;
  const dealPin = state?.pin;

  const [freeze, setFreeze] = useState(null);
  // Re-verify gate (Aug 18, per Ali's report — Option A): mirrors Deal
  // Screen's own verifyingPrice/showBookingLinks/priceCheck pattern
  // exactly, so Skyscanner/Booking.com only appear after a live check
  // against Duffel, same safeguard Deal Screen already has and for the
  // same reason — this page's "Live Now" figure is the last periodic
  // background check, not a live-at-this-second call.
  const [verifyingPrice, setVerifyingPrice] = useState(false);
  const [showBookingLinks, setShowBookingLinks] = useState(false);
  const [priceCheck, setPriceCheck] = useState(null);

  // Destination photo for the new status banner (Aug 17, per Ali's
  // report — folded into this build after reconsidering the earlier
  // "no photos here, this page's job is data not the dream" scoping:
  // that reasoning held for the OLD layout, where the status content
  // was crammed together with the analytical cards; now that they're
  // properly separated, a photo in just the status banner doesn't
  // compete with the chart or Price Intelligence sections the way it
  // would have before. Called unconditionally here, before the
  // freezeId/userId and loading early returns below — freeze starts as
  // null on first render, so this reads undefined initially and the
  // hook's own effect re-runs once freeze.destination actually loads,
  // same pattern as everywhere else this hook is used. dealPin is the
  // fallback for the fromDeal entry path, where the destination name
  // is already known before freeze itself has loaded.
  const statusPhotoUrl = useDestinationPhoto(freeze?.destination || dealPin?.destination);

  const [points, setPoints] = useState([]);
  const [prePoints, setPrePoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  // Proof-of-purchase evidence upload (Aug 25, per Ali's report —
  // Phase A, part 2). Only ever relevant for a 'kept' resolution — a
  // 'refunded' freeze already paid out automatically via the tracked
  // price alone, nothing for evidence to add. evidenceResult holds the
  // backend's response so the UI can show the right one of three real
  // outcomes (declined automatically / pending a human look / an error)
  // rather than a single generic "submitted" state.
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const [evidenceResult, setEvidenceResult] = useState(null);
  const [evidenceError, setEvidenceError] = useState(null);
  // Modal redesign (Sep 14, per Ali's report — real UX issue: the
  // whole evidence section lived far down the page, past itinerary and
  // Day 5 snapshot, so someone who paid more than they froze had no
  // way to know that mattered without scrolling all the way down. A
  // compact prompt now sits directly in the always-visible top ticket
  // card (see the resolved-freeze stub below); tapping it opens this
  // same upload/status content as a modal instead. Nothing about the
  // upload/decision logic itself changed, only where it's triggered
  // from.
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [itinOpen, setItinOpen] = useState(false);
  const [itinEndOpen, setItinEndOpen] = useState(false);
  // Forces a re-render once a minute so the "4d 22h 13m" countdown
  // actually ticks instead of only updating on unrelated re-renders.
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!freezeId || !userId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const [freezesData, pointsData] = await Promise.all([
          getFreezes(userId),
          getFreezePricePoints(freezeId),
        ]);
        if (cancelled) return;
        const match = (freezesData.freezes || []).find(f => f.id === freezeId);
        if (!match) {
          setNotFound(true);
        } else {
          setFreeze(match);
          setPoints(pointsData.points || []);
          setPrePoints(pointsData.pre_freeze_points || []);
          // Real bug found live (Aug 26): evidenceResult was pure local
          // state, set only right after a successful upload in this same
          // browser session — revisiting this screen even seconds later
          // (a fresh mount) showed the blank upload form again as if
          // nothing had ever been submitted, with zero way to check
          // status or know when a refund might land. This fetches
          // whatever's actually on file for this freeze, every load, so
          // the person always sees their real current state (submitted/
          // under review, approved, or declined) rather than what this
          // one component instance happens to remember.
          if (match.status === 'kept') {
            getFreezeEvidence(freezeId)
              .then(res => { if (!cancelled && res.evidence) setEvidenceResult(res.evidence); })
              .catch(() => { /* best-effort — falls back to the upload form, same as before this fix existed */ });
          }
        }
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [freezeId, userId]);

  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Missing state (direct URL hit, refresh, or a bad navigation) —
  // bounce to the Dashboard rather than rendering a broken screen. The
  // Dashboard's own "Your Price Freezes" section is always a valid way
  // back into this same data.
  useEffect(() => {
    // Real fix, not just a symptom patch (Sep 23) — this effect is
    // exactly what bounced every email-opened link before the session
    // lookup above ever got a chance to resolve. Waiting on
    // resolvingSession is what actually lets a deep link work; the
    // freezeId/userId param changes above only supplied the data, this
    // is what stops the page from redirecting away from it.
    if (resolvingSession) return;
    if (!freezeId || !userId || notFound) {
      const t = setTimeout(() => navigate('/dashboard'), notFound ? 1500 : 0);
      return () => clearTimeout(t);
    }
  }, [freezeId, userId, notFound, navigate, resolvingSession]);

  if (!freezeId || !userId) return null;

  if (loading) {
    return (
      <div style={{ background: theme.bg, minHeight: '100vh', padding: '40px 20px', textAlign: 'center' }}>
        <p style={{ color: theme.textMuted, fontSize: '14px' }}>Loading your Price Watch...</p>
      </div>
    );
  }

  if (notFound || !freeze) {
    return (
      <div style={{ background: theme.bg, minHeight: '100vh', padding: '40px 20px', textAlign: 'center' }}>
        <p style={{ color: theme.textMuted, fontSize: '14px' }}>Couldn't find that Price Watch. Heading back to your dashboard.</p>
      </div>
    );
  }

  const skyscannerUrl = buildSkyscannerUrl(freeze);
  const bookingComUrl = buildBookingComUrl(freeze);

  const startedAt = new Date(freeze.started_at);
  // Was new Date(freeze.expires_at) directly (Sep 21, per Ali's
  // report — real bug, caught live: this whole timeline (nudge date,
  // Day N/RESOLUTION date+time, the price chart's "days remaining"
  // and domain end) was built from the nominal expiry only, silently
  // ignoring that resolve_freezes_nearing_departure can resolve a
  // near-term freeze earlier than that — contradicting the honest
  // early-resolution date FreezeDurationModal.js already shows BEFORE
  // purchase. Now uses effective_resolves_at, computed once
  // server-side in get_freezes() from the exact same inputs
  // freeze_resolver.py itself uses, so this can't drift out of sync
  // with the real backend behavior the way a second frontend
  // computation could. Falls back to expires_at for any freeze
  // fetched before this field existed.
  const expiresAt = new Date(freeze.effective_resolves_at || freeze.expires_at);
  const now = new Date();

  // Duration-aware timeline (Sep 9, per Ali's report — real bug,
  // caught live via screenshot: a 48-hour freeze was still showing
  // "Your 5-Day Timeline," "Day 1 of 5," and a "Day 4" nudge step that
  // doesn't exist in a 2-day window. Everything below is now derived
  // from the freeze's own duration_hours instead of a hardcoded 5,
  // matching how started_at/expires_at were already real, not assumed.
  // Falls back to 120 (the original tier) for any freeze created
  // before duration_hours existed as a column — those genuinely were
  // 5-day freezes, so this fallback is accurate, not a guess.
  const durationHours = freeze.duration_hours || 120;
  const durationDays = durationHours / 24;
  // The 24-hour-before-resolution nudge is a fixed offset from
  // expires_at regardless of tier (see freeze_resolver.py's own
  // DAY4_NUDGE_WINDOW_HOURS) — it does NOT scale proportionally with
  // duration, so for the 48-hour tier it lands at hour 24, the
  // halfway point, not on some "Day 4" that doesn't exist in a 2-day
  // freeze. nudgeDayNumber is which real calendar day of THIS freeze
  // that offset actually falls on, computed rather than assumed.
  //
  // UPDATED (Sep 24, per Ali's report): the window is per-duration now
  // — 24h / 12h / 6h for the 5-day / 48-hour / free-trial watch — and
  // the watch gets NO nudge at all when its runway is too short for
  // the heads-up to land a couple of hours after it started. The
  // backend decides both, once, in freeze_pricing.nudge_plan (the same
  // function the email job uses) and sends the result as
  // freeze.nudge_hours (null = no nudge) — so this timeline can never
  // promise an email the job wouldn't send, which is exactly what the
  // free trial's card was doing before this. Deliberately NOT
  // recomputed here from a copy of the rule.
  const nudgeHours = freeze.nudge_hours ?? null;
  const hasNudge = nudgeHours != null;
  const nudgeAt = hasNudge ? new Date(expiresAt.getTime() - nudgeHours * 60 * 60 * 1000) : null;
  const nudgeDayNumber = hasNudge
    ? Math.min(durationDays, Math.max(1, Math.ceil((nudgeAt - startedAt) / (24 * 60 * 60 * 1000))))
    : durationDays;
  const cancelWindowHours = FREEZE_CANCEL_WINDOW_HOURS_BY_DURATION[durationHours] ?? DEFAULT_FREEZE_CANCEL_WINDOW_HOURS;

  // Calendar dates per timeline day — the actual ask here: "Day 2 of
  // 5" alone doesn't let someone correlate the step to a real date on
  // their own calendar. Day 0/last-day use the real
  // started_at/expires_at; the nudge date is derived from the same
  // fixed 24h-before-resolution offset as nudgeDayNumber above.
  const fmtDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  // Exact time added (Sep 16, per Ali's report — real point: the
  // timeline only ever showed a bare date, "Sep 14" / "Sep 16," with
  // no way to know what time within that day a freeze actually locked
  // in or will resolve — and the underlying data already had it the
  // whole time, started_at/expires_at are full timestamps, not just
  // dates, this just never surfaced it). resolvedAt is the real,
  // actual resolution time once the freeze has genuinely resolved —
  // can run a few minutes after the scheduled expiresAt depending on
  // when the resolver job picks it up — falling back to the scheduled
  // expiresAt beforehand, since the real time doesn't exist yet.
  const fmtTime = (d) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const resolvedAt = freeze.resolved_at ? new Date(freeze.resolved_at) : null;
  const day0Date = fmtDate(startedAt);
  const day0Time = fmtTime(startedAt);
  const nudgeDate = hasNudge ? fmtDate(nudgeAt) : null;
  const nudgeTime = hasNudge ? fmtTime(nudgeAt) : null;
  const day5Date = fmtDate(expiresAt);
  const day5Time = fmtTime(resolvedAt || expiresAt);
  const todayDate = fmtDate(now);
  const totalMs = expiresAt - startedAt;
  const elapsedMs = Math.min(Math.max(now - startedAt, 0), totalMs);
  const dayOfFive = Math.min(durationDays, Math.max(1, Math.ceil(elapsedMs / (24 * 60 * 60 * 1000)) || 1));

  const remainingMs = Math.max(0, expiresAt - now);

  const routeLabel = freeze.origin_code && freeze.destination_code
    ? `${freeze.origin_code} → ${freeze.destination_code}`
    : (freeze.destination || 'Trip');
  const dateRange = [freeze.departure_date, freeze.return_date].filter(Boolean).join(' – ');

  // Same re-verify flow as Deal Screen's handleVerifyAndBook (Aug 18,
  // per Ali's report — Option A), adapted for the freeze object's own
  // fields rather than a pin. freeze.flight_preference is already the
  // backend's own key format ('best_value' / 'direct'), unlike Deal
  // Screen's deal.type which is a display label needing translation —
  // one less step needed here.
  const handleVerifyAndBook = async () => {
    setVerifyingPrice(true);
    setPriceCheck(null);
    try {
      const result = await verifyPrice({
        origin: freeze.origin_code,
        destination: freeze.destination_code,
        departure_date: freeze.departure_date,
        return_date: freeze.return_date || null,
        adults: freeze.adults || 1,
        child_ages: freeze.child_ages || [],
        budget: freeze.budget,
        currency: freeze.currency || 'CAD',
        deal_type: freeze.flight_preference || 'best_value',
        previous_price: freeze.current_price,
      });
      setPriceCheck(result);
    } catch (err) {
      console.error('Price re-verification failed:', err);
      setPriceCheck({ status: 'unverified', previous_price: freeze.current_price });
    } finally {
      setVerifyingPrice(false);
      setShowBookingLinks(true);
    }
  };

  const comparisonPrice = freeze.resolved_price ?? freeze.current_price;
  // heldSoFar drives the narrative beat below — null means "no data
  // yet" (points list is empty), true/false is the real signal from
  // the same price_check_log points the chart itself uses.
  const heldSoFar = points.length === 0 ? null : points.every(p => p.price <= freeze.frozen_price);
  const beat = narrativeBeat(dayOfFive, heldSoFar, durationDays);
  const isOverFrozen = comparisonPrice != null && comparisonPrice > freeze.frozen_price;
  const isActive = freeze.status === 'active';

  const hoursSinceStart = (now - startedAt) / (1000 * 60 * 60);
  // !freeze.is_trial added (Sep 24, per Ali's report) — cancelling
  // exists to let someone escape a charge they've decided they don't
  // want; a trial never charges anything regardless of how it
  // resolves, so there's nothing to protect against by offering an
  // early exit. Also sidesteps a real edge case: cancelling a trial
  // already restores the account's one-time eligibility (see
  // cancel_freeze), so leaving this on would let someone start/cancel
  // a trial repeatedly without ever really using it — harmless
  // financially, but pointless to offer.
  const canCancel = isActive && !freeze.is_trial && hoursSinceStart <= cancelWindowHours;
  const cancelHoursLeft = Math.max(0, Math.ceil(cancelWindowHours - hoursSinceStart));

  const handleCancel = async () => {
    if (!window.confirm('Cancel this Price Watch? This can\'t be undone.')) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelFreeze(freeze.id, userId);
      // Re-fetch rather than assume: the resolver job runs every 30
      // minutes and could theoretically resolve this freeze in the
      // narrow window between the cancel request and this reload, so
      // trust the server's current state over a locally-guessed one.
      const freezesData = await getFreezes(userId);
      const match = (freezesData.freezes || []).find(f => f.id === freeze.id);
      if (match) setFreeze(match);
    } catch (error) {
      setCancelError(error?.response?.data?.detail || 'Could not cancel this Price Watch. Try again.');
    } finally {
      setCancelling(false);
    }
  };

  // Aug 25, per Ali's report — Phase A, part 2. Backend decides the
  // real outcome (auto-declined, or held for a human to review); this
  // just surfaces whatever comes back rather than guessing at it
  // client-side. A 400 here most commonly means the 48-hour evidence
  // window already closed — surfaced as the backend's own message
  // rather than a generic failure, since that's actually useful to know.
  const handleEvidenceUpload = async () => {
    if (!evidenceFile) return;
    setEvidenceUploading(true);
    setEvidenceError(null);
    try {
      const result = await uploadFreezeEvidence(freeze.id, evidenceFile);
      setEvidenceResult(result.evidence);
    } catch (error) {
      setEvidenceError(error?.response?.data?.detail || 'Something went wrong — please try again.');
    } finally {
      setEvidenceUploading(false);
    }
  };

  // Timeline step status helper — 'done' | 'active' | 'pending'.
  // step4Reached (kept its name for a minimal diff, but now uses
  // nudgeDayNumber instead of a hardcoded 4 — Sep 9, per Ali's report:
  // the nudge's real day position varies by tier, it's the 48-hour
  // tier's Day 1, not Day 4, which doesn't exist in a 2-day freeze).
  // step5Reached stays exact remainingMs math, already duration-agnostic.
  const step4Reached = dayOfFive >= nudgeDayNumber;
  const step5Reached = remainingMs <= 0;
  const resolved = !isActive; // kept | refunded | cancelled

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '32px' }}>
      <PageBackground variant="calm" />
      <FreezeDetailAnimations />

      {/* This screen previously had no top bar at all — every other app
          screen (Dashboard, DealScreen) has one, so its absence here was
          a real inconsistency, not a design choice. Same navy bar, same
          plain wordmark + accent dot now used consistently across all
          three (Aug 23, per Ali's report — the icon shape itself was
          dropped everywhere in favor of this simpler mark). */}
      <AppNav current="watch" />

      <div style={{ maxWidth: '1300px', margin: '0 auto', padding: '0 20px', position: 'relative' }}>

        {/* ===== Section: Your Price Freeze — status banner, rebuilt
            (Aug 18, per Ali's report) to actually match the approved
            reference: a real side-by-side ticket (white identity
            section | vertical perforation | navy price stub), not the
            single photo-backed navy block with a horizontal divider
            this had before. Photo moves to its own strip above the
            ticket instead of sitting behind the text — same pattern
            already established on Dashboard's cards and DealScreen's
            hero, for the same reason: legibility that doesn't depend
            on how bright a given destination photo happens to be, and
            genuine consistency instead of a banner-specific one-off.
            Every real piece of logic below (justCreated/refunded
            celebration badges, isOverFrozen comparison color, meta.label
            fallback, trust badges) is preserved exactly, just relocated
            into the new structure. ===== */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '20px 0 14px' }}>
          <span style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#4A5F78', whiteSpace: 'nowrap' }}>Your Price Watch</span>
          <span style={{ flex: 1, height: '1px', background: theme.border }} />
        </div>
        {statusPhotoUrl && (
          <div style={{
            height: '140px', borderRadius: '14px', marginBottom: '18px', overflow: 'hidden',
            backgroundImage: `linear-gradient(180deg, rgba(30,58,95,0) 40%, rgba(30,58,95,0.55) 100%), url(${statusPhotoUrl})`,
            backgroundSize: 'cover', backgroundPosition: 'center'
          }} />
        )}
        <div className="ticket-row" style={{ border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', background: theme.surface, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(30,58,95,0.07)' }}>
          <div style={{ flex: 1, minWidth: 0, padding: '24px' }}>
            {justCreated && isActive && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#1E3A5F', color: '#F4C11C',
                borderRadius: '14px', padding: '4px 12px', fontSize: '12px', fontWeight: 700, marginBottom: '10px'
              }}>
                Price Watch started!
              </div>
            )}
            {freeze.status === 'refunded' && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#1E3A5F', color: '#F4C11C',
                borderRadius: '14px', padding: '4px 12px', fontSize: '12px', fontWeight: 700, marginBottom: '10px'
              }}>
                You paid nothing!
              </div>
            )}
            <div style={{ fontSize: '26px', fontWeight: 800, color: theme.textPrimary, fontFamily: theme.fontDisplay, lineHeight: 1.15 }}>{routeLabel}</div>
            {dateRange && (
              <div style={{ fontSize: '12.5px', color: theme.textMuted, marginTop: '4px' }}>
                {dateRange}{isActive ? ` · Today: ${todayDate}` : ''}
              </div>
            )}
            {isActive && (() => {
              const total = expiresAt.getTime() - startedAt.getTime();
              const pct = Math.min(100, Math.max(0, ((now.getTime() - startedAt.getTime()) / total) * 100));
              const left = Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / 3600000));
              const leftTxt = left < 24 ? `${left} hour${left === 1 ? '' : 's'} left` : `${Math.ceil(left / 24)} days left`;
              const when = (d) => d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
              return (
                <div style={{ marginTop: '18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', fontSize: '12px', fontWeight: 700, color: theme.structural, marginBottom: '6px' }}>
                    <span>Started {when(startedAt)}</span>
                    <span>Result {when(expiresAt)} · {leftTxt}</span>
                  </div>
                  {/* Plane rides the head of the bar toward the result (Oct 10, per Ali). */}
                  <div style={{ position: 'relative', height: '8px', borderRadius: '999px', background: '#EAF1F8', margin: '14px 0 12px' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: theme.structural, borderRadius: '999px' }} />
                    <div
                      aria-hidden="true"
                      style={{ position: 'absolute', top: '50%', left: `clamp(15px, ${pct}%, calc(100% - 15px))`, transform: 'translate(-50%, -50%)', width: '30px', height: '30px', borderRadius: '50%', background: theme.action, border: '2px solid #fff', boxShadow: '0 2px 6px rgba(30,58,95,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill={theme.structural}>
                        <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" transform="rotate(90 12 12)" />
                      </svg>
                    </div>
                  </div>
                </div>
              );
            })()}
            {isActive && (
              <div style={{ display: 'flex', gap: '10px', fontSize: '12px', fontWeight: 600, color: theme.textMuted, flexWrap: 'wrap', marginTop: '16px' }}>
                <span>{durationHours === 48 ? '48-hour' : `${durationDays}-day`} window</span><span>·</span><span>Checked every 2 hours</span>
              </div>
            )}
            {/* Same share row as the Deal page header (Oct 10, per Ali). It shares
                the trip's current saved fare (route, dates, price, flights),
                never the watch result or anything about the fee. */}
            {freeze.pin_id && (
              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #EEF1F3' }}>
                <div style={{ fontFamily: theme.fontMono, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#4A5F78', marginBottom: '10px' }}>Take it with you</div>
                <ShareDeal pinId={freeze.pin_id} variant="buttons" />
              </div>
            )}

            {/* Paid-more-than-you-froze prompt — moved here from a
                separate card far down the page (Sep 14, per Ali's
                report — real UX issue: it took real scrolling to reach,
                past itinerary and the Day 5 snapshot, so a real prompt
                to action was easy to miss entirely). Only ever relevant
                for a resolved 'kept' freeze; gone the instant a refund
                has actually happened via evidence (status 'approved'),
                so this never asks someone to do something already done.
                Tapping opens the exact same upload/status content as a
                modal (see showEvidenceModal below) — nothing about the
                underlying evidence logic changed, only where it's
                triggered from. theme.action (gold) matches the same
                "time-boxed action item" color already used for this on
                Dashboard's own evidence banner. */}
            {!isActive && freeze.status === 'kept' && evidenceResult?.status !== 'approved' && (
              <div
                onClick={() => setShowEvidenceModal(true)}
                style={{
                  marginTop: '16px', background: theme.action, padding: '11px 14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                  cursor: 'pointer'
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#1a1a1a' }}>
                    {!evidenceResult
                      ? `Paid more than ${formatMoney(freeze.frozen_price)} ${freeze.currency}?`
                      : evidenceResult.status === 'declined'
                        ? 'Submission reviewed'
                        : 'Submission under review'}
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(26,26,26,0.75)' }}>
                    {!evidenceResult ? "Submit proof, get your fee back" : 'Tap to see the details'}
                  </div>
                </div>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#1a1a1a', background: 'rgba(26,26,26,0.12)', padding: '5px 10px', borderRadius: '14px', whiteSpace: 'nowrap' }}>
                  {!evidenceResult ? 'Submit proof' : 'View →'}
                </span>
              </div>
            )}
          </div>

          <div className="ticket-stub" style={{ width: '300px', background: theme.structural, padding: '22px 24px', color: '#fff' }}>
            {/* "Locked in at" → "Frozen at" (Aug 14, per Ali's
                review): Price Freeze is a bounded-risk fee-refund
                model, not a true price-lock — nothing actually holds
                this fare anywhere. "Frozen at" matches the feature's
                own name without overpromising. */}
            <div style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.7)' }}>
              Starting price
            </div>
            <div style={{ fontFamily: theme.fontMono, fontWeight: 700, fontSize: '30px', color: '#fff', lineHeight: 1.1, marginTop: '4px' }}>
              {formatMoney(Number(freeze.frozen_price))}
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', fontWeight: 700 }}>{freeze.currency}</div>

            {comparisonPrice != null && (
              <div style={{ marginTop: '18px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                <div style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.7)' }}>
                  {isActive ? 'Live now' : 'At resolution'}
                </div>
                <div style={{ fontFamily: theme.fontMono, fontWeight: 700, fontSize: '20px', color: isOverFrozen ? '#F5B199' : '#F4C11C', marginTop: '4px' }}>
                  {formatMoney(comparisonPrice)} {freeze.currency}
                </div>
                {isOverFrozen && (
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                    (up {formatMoney(comparisonPrice - freeze.frozen_price)} since the start)
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {isActive && (
          <WatchVerdict comparisonPrice={comparisonPrice} frozenPrice={freeze.frozen_price} thresholdUp={freeze.threshold_up_local} thresholdDown={freeze.threshold_down_local} feeText={feeLabel(freeze.fee_amount_usd)} currency={freeze.currency} />
        )}

        {/* ===== Two-column: What you'll pay | What happens next (Oct 10,
            per Ali's approved mockup). The fee table used to live inside
            the last timeline step; it now has its own card, with the
            short timeline beside it. All the same wording and states
            (free trial, kept, refunded, cancelled) are kept. ===== */}
        <style>{`
          .fd-cols { display: grid; grid-template-columns: minmax(0, 1fr) 330px; gap: 16px; align-items: stretch; }
          .fd-cols > div { display: flex; flex-direction: column; }
          .fd-cols > div > .fd-card { flex: 1; }
          @media (max-width: 860px) { .fd-cols { grid-template-columns: minmax(0, 1fr); } }
        `}</style>
        <div className="fd-cols">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '28px 0 14px' }}>
              <span style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#4A5F78', whiteSpace: 'nowrap' }}>{freeze.is_trial ? 'Your trial Price Watch' : "What you'll pay"}</span>
              <span style={{ flex: 1, height: '1px', background: theme.border }} />
            </div>
            <div className="fd-card" style={{ background: '#fff', border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {(() => {
                const cur = freeze.currency;
                const day0 = Number(freeze.frozen_price);
                const hasT = freeze.threshold_up_local != null && freeze.threshold_down_local != null;
                const upperP = hasT ? day0 + Number(freeze.threshold_up_local) : null;
                const lowerP = hasT ? day0 - Number(freeze.threshold_down_local) : null;
                const feeUsdN = feeUsdNumber(freeze.fee_amount_usd);
                const feeUsd = feeUsdN == null ? null : feeUsdN.toFixed(2);
                const feeText = feeUsd == null
                  ? (freeze.fee_amount != null ? `${formatMoney(freeze.fee_amount)} ${cur}` : 'the fee')
                  : (freeze.fx_rate_usd_to_local != null && freeze.fee_amount != null
                      ? `${formatMoney(freeze.fee_amount)} ${cur} (US$${feeUsd})`
                      : `US$${feeUsd}`);
                const whenText = expiresAt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
                const rowBase = { display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', padding: '13px 22px', fontSize: '13.5px', borderTop: '1px solid #EEF1F3', alignItems: 'center', color: theme.textPrimary };
                const nothing = <span style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', background: '#E8F4EC', color: '#17663A', padding: '4px 10px', borderRadius: '999px' }}>Nothing</span>;
                const note = { fontSize: '12px', color: theme.textMuted, lineHeight: 1.55, margin: 0 };
                return (
                  <>
                    {freeze.is_trial ? (
                      <div style={{ padding: '18px 22px' }}>
                        <p style={{ ...note, marginBottom: '8px' }}>
                          Nothing to pay, ever. This one's on us, once per account. We're still tracking it against the real result rule ({hasT ? `${formatMoney(upperP)} ${cur} or more above, or ${formatMoney(lowerP)} ${cur} or less` : 'US$5 or more above, or US$20 or more below'} than your starting price of {formatMoney(day0)} {cur}), so you can see how a real watch plays out.
                        </p>
                        <p style={note}>We'll email you what happened when it ends on {whenText}, either way.</p>
                      </div>
                    ) : (
                      <>
                        {hasT ? (
                          <>
                            <div style={{ ...rowBase, borderTop: 'none', background: '#EAF1F8', borderBottom: '3px solid #F4C11C', fontWeight: 800, color: theme.structural }}>
                              <span>If the price on {whenText} is…</span><span>You pay</span>
                            </div>
                            <div style={rowBase}><span>{formatMoney(upperP)} {cur} or more</span>{nothing}</div>
                            <div style={rowBase}><span>{formatMoney(lowerP)} {cur} or less</span>{nothing}</div>
                            <div style={{ ...rowBase, background: (!resolved || freeze.status === 'kept') ? '#F4C11C' : '#fff', fontWeight: (!resolved || freeze.status === 'kept') ? 700 : 400, color: theme.structural }}>
                              <span>Between {formatMoney(lowerP)} and {formatMoney(upperP)} {cur}</span>
                              <b style={{ fontFamily: theme.fontMono }}>{feeText}</b>
                            </div>
                          </>
                        ) : (
                          <p style={{ ...note, padding: '18px 22px 4px' }}>
                            If the price on {whenText} is US$5 or more above, or US$20 or more below, your starting price of {formatMoney(day0)} {cur}, you pay nothing. Anywhere in between, you pay {feeText}.
                          </p>
                        )}
                        <div style={{ padding: '14px 22px', borderTop: '1px solid #EEF1F3' }}>
                          <p style={{ ...note, marginBottom: '8px' }}>
                            Only the price at the end counts. Nothing is charged at the start: we hold the fee on your card and remove the hold if you don't owe anything. We'll email you the result.
                          </p>
                          <p style={note}>
                            <b>Booked and paid more than {formatMoney(day0)} {cur}?</b> If you were charged the fee, send us your booking confirmation within 48 hours of your result. If it shows you paid more than your starting price, we'll refund the fee after a quick review.
                          </p>
                        </div>
                      </>
                    )}

                    {resolved && (
                      <div style={{ padding: '14px 22px', borderTop: '1px solid #EEF1F3', background: freeze.status === 'kept' ? '#F4C11C' : '#F5F8FB' }}>
                        <div style={{ fontSize: '14px', fontWeight: 800, color: theme.structural, marginBottom: '3px' }}>
                          {freeze.status === 'refunded' && 'You paid nothing'}
                          {freeze.status === 'kept' && 'Fee charged'}
                          {freeze.status === 'cancelled' && 'Cancelled by you'}
                        </div>
                        <p style={note}>
                          {freeze.status === 'refunded' && `The price ended outside the fee range around your starting price of ${formatMoney(freeze.frozen_price)} ${freeze.currency}, so you didn't owe anything and the hold on your card was removed.`}
                          {freeze.status === 'kept' && `The price ended inside the fee range around your starting price of ${formatMoney(freeze.frozen_price)} ${freeze.currency}, so the ${feeLabel(freeze.fee_amount_usd)} was charged for the watching.`}
                          {freeze.status === 'cancelled' && 'You cancelled this Price Watch within the cancellation window. Nothing was charged.'}
                        </p>
                      </div>
                    )}

                    {freeze.is_trial && (
                      <div style={{ padding: '0 22px 14px' }}>
                        {freeze.is_trial && (
                          <div style={{ display: 'inline-block', marginTop: '10px', fontSize: '12px', fontWeight: 700, color: '#1E3A5F', background: '#F4C11C', padding: '4px 10px', borderRadius: '999px' }}>
                            Trial Price Watch: never charged, once per account
                          </div>
                        )}
                      </div>
                    )}

                    {/* Liability reminder (Aug 31) — kept in the card that
                        explains the outcomes, as before. Not lawyer-reviewed. */}
                    <p style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.5, margin: 0, marginTop: 'auto', padding: '12px 22px 16px', borderTop: '1px solid #EEF1F3' }}>
                      Price Watch doesn't reserve the fare; it's about the {feeLabel(freeze.fee_amount_usd)} you pay for it. Our responsibility is limited to refunding this fee under our stated terms. See our <a href="https://flyatlast.com/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>terms</a> for details.
                    </p>
                  </>
                );
              })()}
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '28px 0 14px' }}>
              <span style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#4A5F78', whiteSpace: 'nowrap' }}>What happens next</span>
              <span style={{ flex: 1, height: '1px', background: theme.border }} />
            </div>
            <div className="fd-card" style={{ background: '#fff', border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '16px', padding: '20px 22px', display: 'flex', flexDirection: 'column' }}>
              {(() => {
                const GREEN = '#17663A';
                const marker = (state, content) => (
                  <span style={{
                    width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, boxSizing: 'border-box',
                    background: state === 'done' ? GREEN : (state === 'active' ? theme.action : '#fff'),
                    border: state === 'pending' ? '2px solid #C3CFDA' : 'none',
                    color: state === 'done' ? '#fff' : '#1a1a1a',
                    boxShadow: state === 'active' ? '0 0 0 4px rgba(244,193,28,0.25)' : 'none'
                  }}>{content}</span>
                );
                const title = { fontSize: '13.5px', fontWeight: 800, color: theme.structural, lineHeight: 1.3 };
                const sub = { fontSize: '12px', color: theme.textMuted, lineHeight: 1.5, marginTop: '1px' };
                const resultState = resolved ? 'done' : (step5Reached ? 'active' : 'pending');
                return (
                  <div style={{ position: 'relative', display: 'grid', gap: '18px', flex: 1, alignContent: 'space-between' }}>
                    <div style={{ position: 'absolute', left: '10px', top: '12px', bottom: '12px', width: '2px', background: '#E4E9ED' }} />
                    <div style={{ position: 'relative', display: 'flex', gap: '12px' }}>
                      {marker('done', '✓')}
                      <div>
                        <div style={title}>Watch started</div>
                        <div style={sub}>{day0Date}, {day0Time} · {formatMoney(freeze.frozen_price)} {freeze.currency}</div>
                      </div>
                    </div>
                    <div style={{ position: 'relative', display: 'flex', gap: '12px' }}>
                      {resolved ? marker('done', '✓') : marker('active', <span className="fd-pulse-dot">●</span>)}
                      <div style={{ minWidth: 0 }}>
                        <div style={title}>{resolved ? 'Watching for price checks' : beat.title}</div>
                        <div style={sub}>
                          {resolved
                            ? (nudgeDayNumber > 1 ? `Days 1–${nudgeDayNumber - 1} · done` : 'Day 1 · done')
                            : `Day ${dayOfFive} of ${durationDays} · today, ${todayDate}${canCancel ? ` · ${cancelHoursLeft}h left to cancel` : ''}`}
                        </div>
                        {!resolved && <div style={{ ...sub, marginTop: '4px' }}>{beat.body}</div>}
                        {canCancel && (
                          <div
                            onClick={cancelling ? undefined : handleCancel}
                            style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '8px', cursor: cancelling ? 'default' : 'pointer', opacity: cancelling ? 0.6 : 1 }}
                          >
                            <span style={{ fontSize: '12.5px', color: theme.textMuted }}>Changed your mind?</span>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: theme.danger }}>{cancelling ? 'Cancelling...' : 'Cancel watch'}</span>
                          </div>
                        )}
                        {cancelError && <p style={{ fontSize: '12px', color: theme.danger, margin: '6px 0 0' }}>{cancelError}</p>}
                      </div>
                    </div>
                    {hasNudge && (
                      <div style={{ position: 'relative', display: 'flex', gap: '12px' }}>
                        {marker(resolved ? 'done' : (step4Reached ? 'active' : 'pending'), resolved ? '✓' : '')}
                        <div>
                          <div style={title}>{nudgeHours}-hour nudge</div>
                          <div style={sub}>{nudgeDate}, around {nudgeTime} · a reminder email, no surprises</div>
                        </div>
                      </div>
                    )}
                    <div style={{ position: 'relative', display: 'flex', gap: '12px' }}>
                      {marker(resultState, resolved ? '✓' : '')}
                      <div>
                        <div style={title}>
                          {resolved
                            ? (freeze.status === 'refunded' ? 'You paid nothing' : (freeze.status === 'kept' ? 'Fee charged' : 'Cancelled by you'))
                            : 'Result'}
                        </div>
                        <div style={sub}>{day5Date}, {day5Time}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Proof-of-purchase evidence (Aug 25, per Ali's report — Phase
            A, part 2). Only ever relevant for a 'kept' resolution — a
            'refunded' freeze already paid out automatically from the
            tracked price alone, evidence has nothing to add there. This
            is the one path that catches the real gap the whole
            business-model review was about: our tracked price and the
            price someone actually pays can differ, and this is how
            that gap gets closed after the fact rather than never.

            Moved into a modal (Sep 14, per Ali's report — real UX
            issue: this used to sit inline here, far down the page past
            itinerary and the Day 5 snapshot, easy to miss entirely
            without scrolling all the way down). Trigger is now the
            compact prompt in the top ticket card (see showEvidenceModal
            above) — this is the exact same content and logic as
            before, just presented as a modal instead of always-inline.
            Backdrop click and the X both close it; neither the upload
            state nor a submitted result is lost by closing, since both
            live in component state, not the modal's own mount. */}
        {freeze.status === 'kept' && showEvidenceModal && (
          <div
            onClick={() => setShowEvidenceModal(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(26,26,26,0.55)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', border: `2px solid ${theme.structural}`, padding: '22px', maxWidth: '440px', width: '100%', maxHeight: '85vh', overflowY: 'auto' }}
            >
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
                <span onClick={() => setShowEvidenceModal(false)} style={{ cursor: 'pointer', fontSize: '20px', color: theme.textMuted, lineHeight: 1 }}>×</span>
              </div>
              {evidenceResult ? (
                evidenceResult.status === 'declined' ? (
                  <>
                    <div style={{ fontSize: '10.5px', fontWeight: 800, color: '#9c2f2f', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Submission reviewed</div>
                    <p style={{ fontSize: '13px', color: theme.textMuted, lineHeight: 1.6, margin: 0 }}>{evidenceResult.decision_reason || "The price on this doesn't match what we tracked for this route."}</p>
                  </>
                ) : evidenceResult.status === 'approved' ? (
                  // New branch (Aug 26, per Ali's report) — before this,
                  // an approved submission fell into the same catch-all
                  // else as pending_review and would have shown "Under
                  // review" forever, even after the $9.99 was already
                  // refunded via Stripe. Real gap: this state was never
                  // reachable at all until the one-tap email
                  // Approve/Decline links existed, so it had never been
                  // exercised or noticed before now.
                  <>
                    <div style={{ fontSize: '10.5px', fontWeight: 800, color: theme.success, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Refund approved</div>
                    <p style={{ fontSize: '13px', color: theme.textMuted, lineHeight: 1.6, margin: 0 }}>Your {feeLabel(freeze.fee_amount_usd)} has been refunded.</p>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '10.5px', fontWeight: 800, color: '#8a6c0f', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Under review</div>
                    <p style={{ fontSize: '13px', color: theme.textMuted, lineHeight: 1.6, margin: 0 }}>We're checking this against what we tracked for your route. Usually resolved within 1-2 business days — we'll email you either way.</p>
                  </>
                )
              ) : (
                <>
                  <div style={{ fontSize: '10.5px', fontWeight: 800, color: '#8a6c0f', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Paid more than we tracked?</div>
                  <h3 style={{ fontSize: '16px', color: theme.structural, margin: '0 0 8px' }}>Our tracked price held — but if what you actually paid was higher, tell us</h3>
                  <p style={{ fontSize: '12.5px', color: theme.textMuted, lineHeight: 1.6, margin: '0 0 18px' }}>
                    We track this fare closely, but the price you pay depends on where you book. If your real confirmation shows more than {formatMoney(freeze.frozen_price)} {freeze.currency}, upload it and we'll refund your Price Watch fee{feeParen(freeze.fee_amount_usd)}. A screenshot or PDF that clearly shows your route, travel dates, and the total price you paid. You have 48 hours from resolution to submit.
                  </p>
                  <div style={{ border: `2px dashed ${theme.structural}`, background: theme.bg, padding: '20px', textAlign: 'center', marginBottom: '14px' }}>
                    {/* accept list widened to match what the backend
                        actually supports (Sep 16, per Ali's report —
                        real gap: freeze_evidence.py's own extension
                        whitelist already included .heic/.webp, but
                        this input never told the browser those were
                        allowed. HEIC specifically matters here —
                        that's the DEFAULT format an iPhone's own
                        Camera app saves to, so someone snapping a
                        fresh photo specifically to submit as proof
                        could've had it filtered out of the picker
                        entirely, even though the backend would have
                        accepted it fine). */}
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.heic,.webp"
                      onChange={(e) => setEvidenceFile(e.target.files?.[0] || null)}
                      style={{ fontSize: '12.5px' }}
                    />
                  </div>
                  {evidenceError && (
                    <p style={{ fontSize: '12px', color: '#9c2f2f', margin: '0 0 12px' }}>{evidenceError}</p>
                  )}
                  <div
                    onClick={evidenceUploading || !evidenceFile ? undefined : handleEvidenceUpload}
                    style={{
                      background: (!evidenceFile || evidenceUploading) ? theme.inactiveGray : theme.action,
                      color: '#1a1a1a',
                      textAlign: 'center',
                      padding: '13px',
                      fontWeight: 800,
                      fontSize: '13.5px',
                      cursor: (!evidenceFile || evidenceUploading) ? 'default' : 'pointer'
                    }}
                  >
                    {evidenceUploading ? 'Submitting…' : 'Submit for review'}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ===== Section: Your Price History — chart first, Price
            Intelligence stacked full-width below (Aug 18, per Ali's
            report — Option A of two mocked layouts, chosen because the
            chart is the one piece of this whole feature that's actual
            proof rather than a summary of it, so it leads). Replaces
            the earlier side-by-side pairing, which squeezed the real,
            data-dense chart into half the page width and made the
            whole section feel oversized — confirmed from a live
            screenshot before this was mocked and picked. ===== */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '28px 0 14px' }}>
          <span style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#4A5F78', whiteSpace: 'nowrap' }}>Price history</span>
          <span style={{ flex: 1, height: '1px', background: theme.border }} />
        </div>
        <div style={{ background: theme.surface, border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '14px', padding: '26px 30px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '15px', color: theme.structural, fontWeight: 800, marginBottom: '4px' }}>Your watch, tracked</h3>
          <p style={{ fontSize: '12.5px', color: theme.textMuted, marginBottom: '16px' }}>
            Real checks from our own tracking. We don't predict where the price goes. Hover or tap the line to see any check.
          </p>
          <FreezeFullChart
            prePoints={prePoints}
            points={points}
            frozenPrice={freeze.frozen_price}
            frozenCurrency={freeze.currency}
            startedAt={startedAt}
            expiresAt={expiresAt}
            now={now}
            fmtDate={fmtDate}
            thresholdUp={freeze.threshold_up_local}
            thresholdDown={freeze.threshold_down_local}
            thresholdsEstimated={freeze.thresholds_estimated}
            comparisonPrice={comparisonPrice}
            status={freeze.status}
            feeText={feeLabel(freeze.fee_amount_usd)}
          />
        </div>
        <FreezeInsights
          prePoints={prePoints}
          points={points}
          frozenPrice={freeze.frozen_price}
          frozenCurrency={freeze.currency}
          comparisonPrice={comparisonPrice}
          isActive={isActive}
        />

        {/* ===== Section: Your Itinerary — pulled out of the status
            banner entirely, own heading, own single job. ===== */}
        {(freeze.outbound_flight || freeze.return_flight) && (
          <>
            <SectionLabel style={{ margin: '28px 0 14px' }}>Your itinerary</SectionLabel>
            <div style={{ background: theme.surface, border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '14px', padding: '18px 22px' }}>
              {/* Collapsed by default (Oct 10, per Ali) with a big yellow
                  button so the itinerary is easy to find and open. */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: theme.structural }}>Flights at the start of your watch</div>
                  <div style={{ fontSize: '12.5px', color: theme.textMuted, marginTop: '3px' }}>What we found on {day0Date}. Prices change; this is not a guarantee.</div>
                </div>
                <button
                  onClick={() => setItinOpen(v => !v)}
                  style={{ background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '12px', padding: '13px 24px', fontWeight: 800, fontSize: '14.5px', cursor: 'pointer', fontFamily: theme.fontDisplay, whiteSpace: 'nowrap' }}
                >
                  {itinOpen ? 'Hide itinerary ▴' : 'See itinerary ▾'}
                </button>
              </div>
              {itinOpen && (
              <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #DDE3E7' }}>
              {freeze.outbound_flight && (
                <ItineraryLeg label={freeze.return_flight ? 'Outbound' : 'Your flight'} flight={freeze.outbound_flight} />
              )}
              {freeze.return_flight && (
                <ItineraryLeg label="Return" flight={freeze.return_flight} />
              )}

              {/* Caveat line (Aug 14, per Ali's review) — sits right
                  where a customer would reasonably assume clicking
                  Skyscanner/Booking.com/Google Flights below gets them
                  this frozen price. It doesn't: Price Freeze only
                  guarantees the $9.99 fee outcome, not a held fare on
                  any external site. */}
              <p style={{ fontSize: '12px', color: theme.textMuted, margin: '10px 0' }}>
                Prices change — this is what we found at the start of your watch, not a guarantee you'll see this price when you click through.
              </p>

              {freeze.origin_code && freeze.destination_code && freeze.departure_date && (() => {
                const fmtLongDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                let query = `Flights from ${freeze.origin_code} to ${freeze.destination_code} on ${fmtLongDate(freeze.departure_date)}`;
                if (freeze.return_date) query += ` through ${fmtLongDate(freeze.return_date)}`;
                const gflightsUrl = `https://www.google.com/travel/flights?q=${encodeURIComponent(query)}`;
                return (
                  <div style={{ marginTop: '8px' }}>
                    <a
                      href={gflightsUrl} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'block', textAlign: 'center', background: '#fff', color: '#1E3A5F', border: '2px solid #1E3A5F', padding: '10px', borderRadius: '12px', fontSize: '13px', fontWeight: 800, textDecoration: 'none' }}
                    >
                      Find your exact flight on Google Flights →
                    </a>
                    <p style={{ fontSize: '12px', color: theme.textMuted, textAlign: 'center', marginTop: '6px' }}>
                      Once there, use the Times and Airlines filters to narrow down to this exact flight.
                    </p>
                  </div>
                );
              })()}

              {!showBookingLinks && (
                <>
                  <button
                    onClick={handleVerifyAndBook}
                    disabled={verifyingPrice}
                    style={{
                      width: '100%', background: theme.action, color: '#1a1a1a', border: 'none',
                      borderRadius: '14px', padding: '12px', fontWeight: 800, fontSize: '14px',
                      cursor: verifyingPrice ? 'default' : 'pointer', fontFamily: theme.fontDisplay,
                      opacity: verifyingPrice ? 0.7 : 1, marginTop: '8px'
                    }}
                  >
                    {verifyingPrice ? 'Checking current price...' : 'Check price & book →'}
                  </button>
                  <p style={{ fontSize: '12px', color: theme.textMuted, marginTop: '6px', textAlign: 'center' }}>
                    Verifies this price, then shows Skyscanner or Booking.com to book — prices may vary slightly there.
                  </p>
                </>
              )}
              {showBookingLinks && (
                <div style={{ marginTop: '8px' }}>
                  {priceCheck?.status === 'verified' && (() => {
                    const displayCurrency = priceCheck.current_price_converted != null ? priceCheck.budget_currency : priceCheck.currency;
                    const displayCurrent = priceCheck.current_price_converted != null ? priceCheck.current_price_converted.toFixed(2) : priceCheck.current_price;
                    return (
                      <div style={{ background: theme.bg, border: `1px solid ${theme.divider}`, borderRadius: '14px', padding: '10px', marginBottom: '10px', fontSize: '12px' }}>
                        {priceCheck.direction === 'same' && (
                          <span>✓ Confirmed just now — still <strong>{displayCurrent} {displayCurrency}</strong> with our source.</span>
                        )}
                        {priceCheck.direction === 'down' && (
                          <span>Even better — just found <strong>{displayCurrent} {displayCurrency}</strong>.</span>
                        )}
                        {priceCheck.direction === 'up' && (
                          <span>Heads up — this price moved to <strong>{displayCurrent} {displayCurrency}</strong> since we last checked.</span>
                        )}
                      </div>
                    );
                  })()}
                  {priceCheck?.status === 'unverified' && (
                    <div style={{ background: theme.bg, border: `1px solid ${theme.divider}`, borderRadius: '14px', padding: '10px', marginBottom: '10px', fontSize: '12px', color: theme.textMuted }}>
                      Couldn't re-check the live price just now — showing our last-checked price. Confirm on the site you book with.
                    </div>
                  )}
                  {(skyscannerUrl || bookingComUrl) && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {skyscannerUrl && (
                        <a
                          href={skyscannerUrl} target="_blank" rel="noopener noreferrer"
                          style={{ flex: 1, display: 'block', textAlign: 'center', background: '#0770E3', color: '#fff', padding: '11px 0', borderRadius: '14px', fontSize: '13px', fontWeight: 800, textDecoration: 'none' }}
                        >
                          Skyscanner →
                        </a>
                      )}
                      {bookingComUrl && (
                        <a
                          href={bookingComUrl} target="_blank" rel="noopener noreferrer"
                          style={{ flex: 1, display: 'block', textAlign: 'center', background: '#003580', color: '#fff', padding: '11px 0', borderRadius: '14px', fontSize: '13px', fontWeight: 800, textDecoration: 'none' }}
                        >
                          Booking.com →
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}
              </div>
              )}
            </div>
          </>
        )}

        {/* Day 5 snapshot — only shows once actually resolved, using the
            same permanent-snapshot pattern as Day 0 above. Briefly (Sep
            7, per Ali's report) also repeated the Day 0 itinerary
            directly alongside it here, for an easy side-by-side compare
            without scrolling back up. Reverted (Sep 14, per Ali's
            report — real issue, confirmed live: Day 0 was showing up
            twice on this screen, which read as a mistake rather than a
            deliberate compare view). Day 0 now lives in exactly one
            place — the permanent "Your Itinerary" section higher up —
            and this block only ever shows the Day {durationDays}
            resolved snapshot. */}
        {resolved && (freeze.resolved_outbound_flight || freeze.resolved_return_flight) && (
          <div style={{ background: theme.surface, border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '14px', padding: '18px 22px', marginTop: '14px' }}>
            {/* Collapsed by default, same pattern as the start-of-watch card (Oct 12, per Ali). */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '16px', fontWeight: 800, color: theme.structural }}>Flights at the end of your watch</div>
                <div style={{ fontSize: '12.5px', color: theme.textMuted, marginTop: '3px' }}>What we found on {day5Date}. This is the flight behind your {freeze.status === 'refunded' ? 'refund' : 'kept-fee'} result.</div>
              </div>
              <button
                onClick={() => setItinEndOpen(v => !v)}
                style={{ background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '12px', padding: '13px 24px', fontWeight: 800, fontSize: '14.5px', cursor: 'pointer', fontFamily: theme.fontDisplay, whiteSpace: 'nowrap' }}
              >
                {itinEndOpen ? 'Hide itinerary ▴' : 'See itinerary ▾'}
              </button>
            </div>
            {itinEndOpen && (
              <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #DDE3E7' }}>
                {freeze.resolved_outbound_flight && (
                  <ItineraryLeg label={freeze.resolved_return_flight ? 'Outbound' : 'Your flight'} flight={freeze.resolved_outbound_flight} />
                )}
                {freeze.resolved_return_flight && (
                  <ItineraryLeg label="Return" flight={freeze.resolved_return_flight} />
                )}
              </div>
            )}
          </div>
        )}

        {/* Fallback: if this freeze genuinely has no itinerary data at
            all, Skyscanner/Booking.com must still show somewhere —
            same verify-first gate as the main itinerary card above. */}
        {!(freeze.outbound_flight || freeze.return_flight) && (skyscannerUrl || bookingComUrl) && (
          <div style={{ marginTop: '14px' }}>
            {!showBookingLinks && (
              <button
                onClick={handleVerifyAndBook}
                disabled={verifyingPrice}
                style={{
                  width: '100%', background: theme.action, color: '#1a1a1a', border: 'none',
                  borderRadius: '14px', padding: '13px', fontWeight: 800, fontSize: '14px',
                  cursor: verifyingPrice ? 'default' : 'pointer', fontFamily: theme.fontDisplay,
                  opacity: verifyingPrice ? 0.7 : 1
                }}
              >
                {verifyingPrice ? 'Checking current price...' : 'Check price & book →'}
              </button>
            )}
            {showBookingLinks && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {skyscannerUrl && (
                  <a
                    href={skyscannerUrl} target="_blank" rel="noopener noreferrer"
                    style={{ flex: 1, display: 'block', textAlign: 'center', background: '#0770E3', color: '#fff', padding: '12px 0', borderRadius: '14px', fontSize: '13px', fontWeight: 800, textDecoration: 'none' }}
                  >
                    Skyscanner →
                  </a>
                )}
                {bookingComUrl && (
                  <a
                    href={bookingComUrl} target="_blank" rel="noopener noreferrer"
                    style={{ flex: 1, display: 'block', textAlign: 'center', background: '#003580', color: '#fff', padding: '12px 0', borderRadius: '14px', fontSize: '13px', fontWeight: 800, textDecoration: 'none' }}
                  >
                    Booking.com →
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        <TripExtras pin={{ destination: freeze.destination, departure_date: freeze.departure_date, return_date: freeze.return_date, adults: freeze.adults, child_ages: freeze.child_ages }} />

      </div>
    </div>
  );
}

export default FreezeDetailScreen;